// eslint-disable-next-line import/no-unresolved
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import PropTypes from 'prop-types';
import React, { useContext, useRef, useEffect } from 'react';

import { HeaderLogo, Icon } from '@powdr/components';
import { AbsolutePositions, AlignmentShorthand, Components } from '@powdr/constants';
import { AppContext } from '@powdr/context';
import { useIsInView } from '@powdr/hooks';
import { isBuildTime, slugify } from '@powdr/utils';

import {
  ChildrenWrapper,
  ComponentDrawerHeader, DrawerCloseButton, InterceptPoint, LogoWrapper, StyledComponentDrawer,
} from './styles';

export const ComponentDrawer = ({
  isOpen,
  slideInFrom,
  closeDrawerFunc,
  closeButtonPosition = AlignmentShorthand.TOP_RIGHT,
  closeButtonLabel = null,
  componentTheme = Components.COMPONENT_DRAWER,
  isShowLogoHeader = false,
  isForceShadow = false,
  className,
  children,
}) => {
  const { isMobile } = useContext(AppContext);
  const interceptRef = useRef();
  const drawerRef = useRef();
  const isShowHeaderShadow = useIsInView(interceptRef);

  useEffect(() => () => {
    clearAllBodyScrollLocks();
  }, []);

  useEffect(() => {
    if (drawerRef.current && !isOpen) {
      drawerRef.current.setAttribute('inert', '');
      drawerRef.current.blur();
    } else {
      if (!isBuildTime()) {
        disableBodyScroll(drawerRef?.current, {
          allowTouchMove: (el) => {
            while (el && el !== document?.body) {
              if (el.classList.contains('body-scroll-lock-ignore')) {
                return true;
              }

              el = el.parentElement;
            }
          },
        });
      }

      drawerRef.current.removeAttribute('inert');
      drawerRef.current.focus();
    }
  }, [isOpen, drawerRef]);

  const handleComponentDrawerClose = () => {
    clearAllBodyScrollLocks();
    enableBodyScroll(drawerRef?.current);
    closeDrawerFunc();
  };

  return (
    <StyledComponentDrawer
      ref={drawerRef}
      tabIndex={0}
      aria-label="Beginning of drawer, use Tab to move to other components"
      aria-hidden={!isOpen}
      className={`${className} body-scroll-lock-ignore`}
      $componentTheme={componentTheme}
      $isOpen={isOpen || false}
      $slideInFrom={slideInFrom || AbsolutePositions.RIGHT}
    >
      {(isShowLogoHeader) ? (
        <ComponentDrawerHeader
          $isShowShadow={!isShowHeaderShadow || isForceShadow}
          $componentTheme={componentTheme}
        >
          <LogoWrapper>
            <HeaderLogo isFromDrawer />
          </LogoWrapper>
          {/* Close Button */}
          <DrawerCloseButton
            id={slugify(closeButtonLabel || 'Close Drawer')}
            aria-label={closeButtonLabel || 'Close Drawer'}
            onClick={handleComponentDrawerClose}
            $position={closeButtonPosition || AlignmentShorthand.TOP_RIGHT}
            $componentTheme={componentTheme}
          >
            <Icon className="drawer-close-button" name="ui-close" width={(isMobile) ? 25 : 40} />
          </DrawerCloseButton>
        </ComponentDrawerHeader>
      ) : (
        <DrawerCloseButton
          id={slugify(closeButtonLabel || 'Close Drawer')}
          aria-label={closeButtonLabel || 'Close Drawer'}
          onClick={handleComponentDrawerClose}
          $position={closeButtonPosition || AlignmentShorthand.TOP_RIGHT}
          $componentTheme={componentTheme}
          $isShowShadow={!isShowHeaderShadow || isForceShadow}
        >
          <Icon className="drawer-close-button" name="ui-close" width={(isMobile) ? 25 : 40} />
        </DrawerCloseButton>
      )}
      <ChildrenWrapper $isShowLogoHeader={isShowLogoHeader}>
        <InterceptPoint ref={interceptRef} />
        {children}
      </ChildrenWrapper>
    </StyledComponentDrawer>
  );
};

ComponentDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  slideInFrom: PropTypes.oneOf(Object.values(AbsolutePositions)).isRequired,
  closeButtonPosition: PropTypes.oneOf(Object.values(AlignmentShorthand)),
  closeButtonLabel: PropTypes.string,
  componentTheme: PropTypes.string,
  isShowLogoHeader: PropTypes.bool,
  isForceShadow: PropTypes.bool,
  closeDrawerFunc: PropTypes.func.isRequired,
  className: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
